import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCopy, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import {
  requestWalletFundingDetails,
  verifyBankTransfer,
  type WalletFundingDetails,
} from "../../pages/SalesOfficer/walletFunding";
import {
  createCheckoutReference,
  abandonCheckoutReference,
  type CheckoutReferencePayload,
} from "../../redux/payment/checkoutReference";

type Step = "amount" | "transfer" | "verifying";

type FundWalletModalProps = {
  open: boolean;
  customerEmail: string;
  customerUserId: string;
  shortfall: number;
  grandTotal: number;
  walletBalance: number;
  formatNaira: (value: number) => string;
  orderPayload: CheckoutReferencePayload;
  onClose: () => void;
  onFundComplete: () => void | Promise<void>;
  onCheckoutRefCreated: (refId: string) => void;
};

const POLL_INTERVAL_MS = 5_000;
const POLL_MAX_ATTEMPTS = 36; // 36 * 5s = 3 minutes

function parseAmountInput(raw: string): number | null {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
}

function secondsUntil(isoDate: string): number {
  const ms = new Date(isoDate).getTime() - Date.now();
  return Math.max(0, Math.floor(ms / 1000));
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

const FundWalletModal = ({
  open,
  customerEmail,
  customerUserId,
  shortfall,
  grandTotal,
  walletBalance,
  formatNaira,
  orderPayload,
  onClose,
  onFundComplete,
  onCheckoutRefCreated,
}: FundWalletModalProps) => {
  const [step, setStep] = useState<Step>("amount");
  const [amountRaw, setAmountRaw] = useState("");
  const [details, setDetails] = useState<WalletFundingDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [expired, setExpired] = useState(false);
  const [pollStatus, setPollStatus] = useState<"idle" | "polling" | "timeout" | "success">("idle");
  const [checkoutRef, setCheckoutRef] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
    timerRef.current = null;
    pollRef.current = null;
  }, []);

  const resetState = useCallback(() => {
    clearTimers();
    setStep("amount");
    setDetails(null);
    setError(null);
    setLoading(false);
    setCountdown(0);
    setExpired(false);
    setPollStatus("idle");
    setCheckoutRef(null);
    pollCountRef.current = 0;
    setAmountRaw(String(Math.max(0, Math.ceil(shortfall))));
  }, [shortfall, clearTimers]);

  useEffect(() => {
    if (open) resetState();
    return clearTimers;
  }, [open, resetState, clearTimers]);

  useEffect(() => {
    if (!open) return;
    setAmountRaw(String(Math.max(0, Math.ceil(shortfall))));
  }, [shortfall, open]);

  const startCountdown = useCallback((expiresAt: string) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const initial = secondsUntil(expiresAt);
    setCountdown(initial);
    setExpired(initial <= 0);
    if (initial <= 0) return;

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          setExpired(true);
          return 0;
        }
        return next;
      });
    }, 1000);
  }, []);

  const parsedAmount = parseAmountInput(amountRaw);
  const minAmount = Math.max(0, Math.ceil(shortfall));
  const amountValid =
    parsedAmount !== null && parsedAmount >= minAmount && parsedAmount > 0;

  const fee = details?.fee ?? 50;
  const totalToTransfer = parsedAmount !== null ? parsedAmount + fee : 0;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleContinue = async () => {
    if (!amountValid || parsedAmount === null) {
      toast.error(`Enter at least ${formatNaira(minAmount)}`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const refId = await createCheckoutReference(customerUserId, orderPayload);
      setCheckoutRef(refId);
      onCheckoutRefCreated(refId);

      const metadata: Record<string, unknown> = {
        source: "foodbank_checkout_ao_shortfall",
        paymentPurpose: "wallet_topup_for_checkout_ao",
        checkoutReference: refId,
        userId: customerUserId,
        amount: parsedAmount,
        shortfallAmount: parsedAmount,
        screen: "Checkout/AddAmountModal",
      };

      const res = await requestWalletFundingDetails(customerEmail, parsedAmount, metadata);
      setDetails(res);
      setStep("transfer");
      setPollStatus("idle");
      setExpired(false);
      if (res.expiresAt) {
        startCountdown(res.expiresAt);
      }
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Could not generate transfer account";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    clearTimers();
    setPollStatus("idle");
    pollCountRef.current = 0;
    setExpired(false);
    if (checkoutRef) {
      void abandonCheckoutReference(customerUserId);
      setCheckoutRef(null);
    }
    await handleContinue();
  };

  const startPolling = useCallback(() => {
    if (!details?.reference || !customerUserId) return;

    setPollStatus("polling");
    pollCountRef.current = 0;

    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;

      try {
        const res = await verifyBankTransfer({
          userId: customerUserId,
          reference: details.reference!,
        });

        const status = res?.status || res?.data?.status;
        if (status === "success" || status === true || res?.message?.toLowerCase().includes("success")) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus("success");
          toast.success("Transfer confirmed!");
          void onFundComplete();
          return;
        }
      } catch {
        // Network hiccup during polling — keep retrying
      }

      if (pollCountRef.current >= POLL_MAX_ATTEMPTS) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setPollStatus("timeout");
      }
    }, POLL_INTERVAL_MS);
  }, [details, customerUserId, onFundComplete]);

  const handleConfirmTransfer = () => {
    setStep("verifying");
    startPolling();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-[2.25rem] sm:rounded-[2rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-gray-900 tracking-tight">
              {step === "amount" && "Insufficient wallet balance"}
              {step === "transfer" && "Fund wallet"}
              {step === "verifying" && "Verifying transfer"}
            </h3>
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-2">
              {step === "amount" && "Top up to continue"}
              {step === "transfer" && "Bank transfer"}
              {step === "verifying" && "Please wait"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              clearTimers();
              if (checkoutRef) {
                void abandonCheckoutReference(customerUserId);
              }
              onClose();
            }}
            className="p-2 -mr-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <FiX className="text-xl text-gray-700" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6">
          {/* STEP: AMOUNT */}
          {step === "amount" && (
            <>
              <div className="bg-amber-50/80 rounded-2xl p-4 border border-amber-100 space-y-2">
                <p className="text-sm text-gray-800 leading-relaxed">
                  Order total{" "}
                  <span className="font-black">{formatNaira(grandTotal)}</span> exceeds
                  wallet{" "}
                  <span className="font-black">{formatNaira(walletBalance)}</span>. Add at
                  least{" "}
                  <span className="font-black text-[#00A651]">
                    {formatNaira(shortfall)}
                  </span>{" "}
                  to complete this order with your wallet.
                </p>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="fund-amount"
                  className="text-[11px] font-black text-gray-900 uppercase tracking-widest block"
                >
                  Amount to fund (NGN)
                </label>
                <input
                  id="fund-amount"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0"
                  className="w-full border-2 border-gray-100 rounded-2xl p-4 text-lg font-black text-gray-900 focus:outline-none focus:border-[#00A651]/40"
                  value={amountRaw}
                  onChange={(e) => setAmountRaw(e.target.value)}
                />
                <p className="text-[10px] text-gray-400 font-medium">
                  Minimum {formatNaira(minAmount)}. You can enter more to leave extra
                  balance. A {formatNaira(50)} transaction fee will be added.
                </p>
                {error && (
                  <p className="text-xs text-red-600 font-medium">{error}</p>
                )}
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="button"
                  disabled={loading || !amountValid}
                  onClick={handleContinue}
                  className="w-full py-4 bg-[#00A651] text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-[#008c44] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-100"
                >
                  {loading ? (
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                  ) : (
                    "Continue to bank details"
                  )}
                </button>
              </div>
            </>
          )}

          {/* STEP: TRANSFER DETAILS */}
          {step === "transfer" && details && parsedAmount !== null && (
            <>
              {details.expiresAt && (
                <div className="text-center space-y-1">
                  {expired ? (
                    <p className="text-sm font-black text-red-500 uppercase tracking-widest">
                      Account expired
                    </p>
                  ) : (
                    <>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                        Account expires in
                      </p>
                      <div className="text-3xl font-black text-red-500 tracking-widest tabular-nums">
                        {formatCountdown(countdown)}
                      </div>
                    </>
                  )}
                </div>
              )}

              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest text-center leading-relaxed">
                Transfer exactly{" "}
                <span className="text-gray-900 font-black">
                  {formatNaira(totalToTransfer)}
                </span>{" "}
                to the account below.
              </p>

              <div className="bg-green-50/30 rounded-[2rem] p-6 border border-green-100/50 space-y-6">
                <div className="flex justify-between items-start gap-3">
                  <div className="space-y-1 min-w-0">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Account number
                    </span>
                    <p className="text-lg font-black text-gray-900 tracking-tight break-all">
                      {details.accountNumber}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(details.accountNumber)}
                    className="p-3 bg-white rounded-xl text-green-600 shadow-sm border border-green-50 hover:scale-105 transition-all flex-shrink-0"
                    aria-label="Copy account number"
                  >
                    <FiCopy />
                  </button>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    Bank name
                  </span>
                  <p className="text-lg font-black text-gray-900">
                    {details.bankName}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    Account name
                  </span>
                  <p className="text-lg font-black text-gray-900 uppercase">
                    {details.accountName}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500 font-medium">
                    Funding amount
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {formatNaira(parsedAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500 font-medium">
                    Transaction fee
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {formatNaira(fee)}
                  </span>
                </div>
                <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-base font-black text-gray-900">
                    Total to transfer
                  </span>
                  <span className="text-base font-black text-[#00A651]">
                    {formatNaira(totalToTransfer)}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {expired ? (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleRegenerate}
                    className="w-full py-4 bg-orange-500 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-orange-600 transition-all shadow-lg shadow-orange-100"
                  >
                    {loading ? (
                      <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                    ) : (
                      "Generate new account"
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleConfirmTransfer}
                    className="w-full py-4 bg-[#00A651] text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-[#008c44] transition-all shadow-lg shadow-green-100"
                  >
                    I have transferred the money
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    clearTimers();
                    if (checkoutRef) {
                      void abandonCheckoutReference(customerUserId);
                      setCheckoutRef(null);
                    }
                    setStep("amount");
                    setDetails(null);
                    setError(null);
                    setExpired(false);
                    setPollStatus("idle");
                  }}
                  className="w-full py-2 text-[11px] font-black text-orange-600 uppercase tracking-widest hover:underline"
                >
                  Back to amount
                </button>
              </div>
            </>
          )}

          {/* STEP: VERIFYING */}
          {step === "verifying" && (
            <div className="py-8 text-center space-y-6">
              {pollStatus === "polling" && (
                <>
                  <div className="w-14 h-14 border-4 border-green-200 border-t-[#00A651] rounded-full animate-spin mx-auto" />
                  <div>
                    <h4 className="text-lg font-black text-gray-900">
                      Verifying your transfer
                    </h4>
                    <p className="text-sm text-gray-500 font-medium mt-2 leading-relaxed">
                      Checking with the bank. This may take a moment...
                    </p>
                  </div>
                </>
              )}

              {pollStatus === "success" && (
                <>
                  <div className="w-14 h-14 bg-green-50 border-2 border-green-200 rounded-full flex items-center justify-center mx-auto">
                    <svg
                      className="w-7 h-7 text-[#00A651]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-gray-900">
                      Transfer confirmed
                    </h4>
                    <p className="text-sm text-gray-500 font-medium mt-2">
                      Your wallet has been funded. Returning to checkout...
                    </p>
                  </div>
                </>
              )}

              {pollStatus === "timeout" && (
                <>
                  <div className="w-14 h-14 bg-amber-50 border-2 border-amber-200 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-2xl font-black text-amber-500">!</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-gray-900">
                      Transfer not confirmed yet
                    </h4>
                    <p className="text-sm text-gray-500 font-medium mt-2 leading-relaxed px-4">
                      Your wallet will update once the bank confirms the transfer.
                      You can close this and retry checkout.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        void onFundComplete();
                      }}
                      className="w-full py-4 bg-[#00A651] text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-[#008c44] transition-all shadow-lg shadow-green-100"
                    >
                      Close and retry checkout
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPollStatus("idle");
                        startPolling();
                      }}
                      className="w-full py-2 text-[11px] font-black text-[#00A651] uppercase tracking-widest hover:underline"
                    >
                      Keep checking
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FundWalletModal;
